import { resolveApiOrigin } from '../../api/api-origin.js';
import { useIsMutating } from '@tanstack/react-query';
import { useState } from 'react';
import {
  dashboardDraftMutationKey,
  useDashboard,
  useUpdateDashboard,
  useKioskTokens,
  useMintKioskToken,
  useRevokeKioskToken,
  type DashboardDefinition,
} from '../../api/dashboards.js';
import { AiDashboardDesigner } from './AiDashboardDesigner.js';
import { WidgetDashboardDesigner } from './widgets/WidgetDashboardDesigner.js';
import { designerSource } from './useDraftAutosave.js';
import { refreshLabel } from './refresh-label.js';
import { StandardPanel } from './StandardPanel.js';
import { useIsInternalAdmin } from '../../api/me.js';

export type DashboardDetailTab = 'design' | 'tokens' | 'settings';
type Tab = DashboardDetailTab;

interface Props {
  dashboardId: string;
  /** Controlled tab, so it survives switching dashboards (the parent keys
   *  this component per dashboard). Uncontrolled when omitted. */
  tab?: Tab;
  onTabChange?(tab: Tab): void;
}

export function DashboardDetail({ dashboardId, tab: tabProp, onTabChange }: Props): JSX.Element {
  // CSAGE-496: a designer seeds its state once, from this data, and saves
  // over the draft on the first edit — so it must not start from a cache
  // entry that predates a draft saved elsewhere (MCP, another author).
  // Refetch on every mount, and open a designer only when that fetch has
  // landed, no refetch is running and no draft save is in flight (one
  // flushed by the previous designer's unmount must land first, and a
  // discard too). A FAILED mount refetch never opens it: React Query counts
  // a failure as "fetched after mount" too, and the data left behind is the
  // stale entry this gate exists to avoid (PR #148 review). Once open, the
  // designer stays mounted through later refetches and saves, including a
  // refetch that fails.
  const { data, isLoading, isError, isFetchedAfterMount, isFetching } = useDashboard(dashboardId, {
    refetchOnMount: 'always',
  });
  const savingDraft = useIsMutating({ mutationKey: dashboardDraftMutationKey(dashboardId) });
  const [ownTab, setOwnTab] = useState<Tab>('design');
  const tab = tabProp ?? ownTab;
  const setTab = (next: Tab): void => {
    setOwnTab(next);
    onTabChange?.(next);
  };
  const [designerOpen, setDesignerOpen] = useState(false);
  const designerReady = isFetchedAfterMount && !isFetching && !isError && savingDraft === 0;
  if (tab !== 'design' && designerOpen) setDesignerOpen(false);
  if (tab === 'design' && !designerOpen && designerReady) setDesignerOpen(true);

  if (isLoading) return <div className="card card-body">Loading dashboard…</div>;
  // No data at all, or a failed fetch before the designer opened (its
  // cached data may be stale). A failed background refetch while the
  // designer is open keeps it and its state.
  if (!data || (isError && !designerOpen)) {
    return (
      <div className="card">
        <div className="card-body">
          <div className="alert alert--danger">Dashboard unavailable.</div>
        </div>
      </div>
    );
  }
  const currentVersion = data.currentVersion;
  // CSAGE-496: open the unpublished draft when there is one.
  const source = designerSource(data);
  if (!currentVersion || !source) {
    return (
      <div className="card">
        <div className="card-body">
          <h2>{data.name}</h2>
          <p className="text-dim">No published version yet.</p>
        </div>
      </div>
    );
  }

  return (
    <article className="card dashboard-detail-card">
      {/* Compact single-row header: title + inline meta on the left,
          mode tabs pushed to the right. Saves a full row of vertical
          chrome compared to the old split title+tabs layout. */}
      <header className="dashboard-header">
        <div className="dashboard-header-title">
          <h2 title={data.description ? `${data.name}\n\n${data.description}` : data.name}>
            {data.name}
          </h2>
          <div className="dashboard-header-meta">
            <span className="dashboard-header-badge">v{currentVersion.version}</span>
            <span className={'dashboard-header-mode dashboard-header-mode--' + data.displayMode}>
              {data.displayMode === 'kiosk' ? 'Kiosk' : 'Dashboard'}
            </span>
            <span className="dashboard-header-refresh">
              {refreshLabel(data.refreshIntervalSec)}
            </span>
          </div>
        </div>
        <div className="dashboard-header-tabs" role="tablist">
          <TabBtn active={tab === 'design'} onClick={() => setTab('design')}>
            Design
          </TabBtn>
          <TabBtn active={tab === 'tokens'} onClick={() => setTab('tokens')}>
            Tokens
          </TabBtn>
          <TabBtn active={tab === 'settings'} onClick={() => setTab('settings')}>
            Settings
          </TabBtn>
        </div>
      </header>
      {data.description && (
        <div className="dashboard-header-description" title={data.description}>
          {data.description}
        </div>
      )}

      {/* Keyed by dashboard: each designer seeds its state (and its
          auto-save baseline) once, so it must remount when the selection
          changes — otherwise a cached switch kept the previous
          dashboard's widgets and the next edit saved them into this
          dashboard's draft (CSAGE-496). */}
      {tab === 'design' && !designerOpen && <div className="card-body">Loading dashboard…</div>}
      {tab === 'design' &&
        designerOpen &&
        (data.layoutKind === 'widgets' ? (
          <WidgetDashboardDesigner
            key={dashboardId}
            dashboardId={dashboardId}
            definition={source.definition}
            draftSavedAt={source.draftSavedAt}
            displayMode={data.displayMode}
            refreshIntervalSec={data.refreshIntervalSec}
            currentVersion={currentVersion.version}
          />
        ) : (
          <AiDashboardDesigner
            key={dashboardId}
            dashboardId={dashboardId}
            definition={source.definition}
            draftSavedAt={source.draftSavedAt}
            displayMode={data.displayMode}
            refreshIntervalSec={data.refreshIntervalSec}
            currentVersion={currentVersion.version}
          />
        ))}
      {tab === 'tokens' && <TokensTab dashboardId={dashboardId} />}
      {tab === 'settings' && (
        <SettingsTab
          dashboardId={dashboardId}
          current={data}
          definition={data.currentVersion?.definition ?? null}
        />
      )}
    </article>
  );
}

function TabBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick(): void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button type="button" className={'tab' + (active ? ' tab--active' : '')} onClick={onClick}>
      {children}
    </button>
  );
}

// ─── Tokens tab ─────────────────────────────────────────────────────

function TokensTab({ dashboardId }: { dashboardId: string }): JSX.Element {
  const tokens = useKioskTokens(dashboardId);
  const mint = useMintKioskToken(dashboardId);
  const revoke = useRevokeKioskToken(dashboardId);
  const [label, setLabel] = useState('');
  const [fresh, setFresh] = useState<{ token: string; id: string } | null>(null);

  async function onMint(): Promise<void> {
    const result = await mint.mutateAsync({ label });
    setFresh({ token: result.token, id: result.id });
    setLabel('');
  }

  function copyToClipboard(text: string): void {
    void navigator.clipboard.writeText(text);
  }

  return (
    <div className="card-body stack-lg">
      <div>
        <div className="section-head">
          <div className="section-head-title">
            <h2>Kiosk tokens</h2>
            <p>
              Long-lived, read-only access tokens. Paste the URL into a Chrome kiosk or Fire Stick.
            </p>
          </div>
        </div>

        <div className="card" style={{ background: 'var(--color-surface-2)' }}>
          <div className="card-body">
            <div className="row" style={{ gap: 'var(--space-3)' }}>
              <div className="field" style={{ flex: 1, margin: 0 }}>
                <label className="field-label">Label</label>
                <input
                  className="input"
                  placeholder="Lobby TV · NOC row 2 · etc."
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                />
              </div>
              <button
                type="button"
                className="btn btn--primary"
                disabled={mint.isPending}
                onClick={onMint}
                style={{ alignSelf: 'flex-end' }}
              >
                {mint.isPending ? 'Minting…' : 'Mint token'}
              </button>
            </div>
            {fresh && (
              <div className="alert alert--success" style={{ marginTop: 'var(--space-3)' }}>
                <div style={{ flex: 1 }}>
                  <strong>Token created — copy it now.</strong>
                  <div className="text-sm" style={{ marginTop: 6 }}>
                    We hash the token before persisting. You won't see this value again.
                  </div>
                  <div className="row" style={{ gap: 6, marginTop: 8 }}>
                    <code
                      style={{
                        flex: 1,
                        padding: '8px 10px',
                        background: 'rgba(0,0,0,0.4)',
                        borderRadius: 6,
                        wordBreak: 'break-all',
                      }}
                    >
                      {fresh.token}
                    </code>
                    <button
                      type="button"
                      className="btn btn--secondary btn--sm"
                      onClick={() => copyToClipboard(fresh.token)}
                    >
                      Copy
                    </button>
                  </div>
                  <div className="text-sm" style={{ marginTop: 12 }}>
                    Full kiosk URL:
                    <div className="row" style={{ gap: 6, marginTop: 4 }}>
                      {(() => {
                        // Anchor the kiosk URL on the API origin in
                        // split-host deploys (api.sage.gocanopy.io). The
                        // kiosk endpoint is an API route; resolving
                        // relative would hit the SPA host's nginx, which
                        // falls back to serving index.html — and the
                        // user would copy a link that loads the SPA,
                        // not the dashboard.
                        const apiOrigin = resolveApiOrigin().replace(/\/+$/u, '');
                        const fullUrl = `${apiOrigin || window.location.origin}/reporting/api/kiosk/dashboards/${dashboardId}/view?token=${fresh.token}`;
                        return (
                          <>
                            <code
                              style={{
                                flex: 1,
                                padding: '8px 10px',
                                background: 'rgba(0,0,0,0.4)',
                                borderRadius: 6,
                                wordBreak: 'break-all',
                              }}
                            >
                              {fullUrl}
                            </code>
                            <button
                              type="button"
                              className="btn btn--secondary btn--sm"
                              onClick={() => copyToClipboard(fullUrl)}
                            >
                              Copy URL
                            </button>
                            <a
                              href={fullUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="btn btn--primary btn--sm"
                            >
                              Open
                            </a>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div>
        <div className="section-head">
          <div className="section-head-title">
            <h2>Active tokens</h2>
          </div>
        </div>
        {tokens.isLoading && <div className="text-dim">Loading…</div>}
        {tokens.data && tokens.data.length === 0 && (
          <div className="empty-state" style={{ border: 'none', padding: 'var(--space-5)' }}>
            <div className="empty-state-icon">◯</div>
            <h3>No tokens yet</h3>
            <p>Mint one above to enable kiosk access.</p>
          </div>
        )}
        {tokens.data && tokens.data.length > 0 && (
          <table className="data-table">
            <thead>
              <tr>
                <th>Label</th>
                <th>Created</th>
                <th>Last used</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {tokens.data.map((t) => (
                <tr key={t.id}>
                  <td>
                    <strong>{t.label || <span className="text-mute">—</span>}</strong>
                  </td>
                  <td className="text-dim text-sm">{new Date(t.createdAt).toLocaleString()}</td>
                  <td className="text-dim text-sm">
                    {t.lastUsedAt ? (
                      new Date(t.lastUsedAt).toLocaleString()
                    ) : (
                      <span className="text-mute">never</span>
                    )}
                  </td>
                  <td>
                    {t.revokedAt ? (
                      <span className="badge badge--neutral">Revoked</span>
                    ) : t.expiresAt && new Date(t.expiresAt).getTime() <= Date.now() ? (
                      <span className="badge badge--warning">Expired</span>
                    ) : (
                      <span className="badge badge--success">
                        <span className="badge-dot" /> Active
                      </span>
                    )}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {!t.revokedAt && (
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        disabled={revoke.isPending}
                        onClick={() => {
                          if (confirm(`Revoke token "${t.label || t.id.slice(0, 8)}"?`))
                            revoke.mutate(t.id);
                        }}
                      >
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ─── Settings tab ───────────────────────────────────────────────────

function SettingsTab({
  dashboardId,
  current,
  definition,
}: {
  dashboardId: string;
  current: {
    name: string;
    displayMode: 'dashboard' | 'kiosk';
    refreshIntervalSec: number;
  };
  definition: DashboardDefinition | null;
}): JSX.Element {
  const update = useUpdateDashboard(dashboardId);
  // CSAGE-583: managing standard dashboards is for internal admins only.
  const internalAdmin = useIsInternalAdmin();
  const [name, setName] = useState(current.name);
  const [displayMode, setDisplayMode] = useState(current.displayMode);
  const [refresh, setRefresh] = useState(current.refreshIntervalSec);
  const dirty =
    name !== current.name ||
    displayMode !== current.displayMode ||
    refresh !== current.refreshIntervalSec;

  return (
    <div className="card-body stack-lg">
      <div className="section-head">
        <div className="section-head-title">
          <h2>Settings</h2>
          <p>Display mode and refresh cadence. Does not create a new version.</p>
        </div>
        <button
          type="button"
          className="btn btn--primary btn--sm"
          disabled={!dirty || update.isPending}
          onClick={() => update.mutate({ name, displayMode, refreshIntervalSec: refresh })}
        >
          {update.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
      <label className="field">
        <span className="field-label">Name</span>
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
      </label>
      <div className="grid-2">
        <label className="field" style={{ margin: 0 }}>
          <span className="field-label">Display mode</span>
          <select
            className="select"
            value={displayMode}
            onChange={(e) => setDisplayMode(e.target.value as 'dashboard' | 'kiosk')}
          >
            <option value="kiosk">Kiosk (full-screen, NOC wall)</option>
            <option value="dashboard">Dashboard (interactive)</option>
          </select>
        </label>
        <label className="field" style={{ margin: 0 }}>
          <span className="field-label">Refresh interval (sec)</span>
          <input
            type="number"
            min={0}
            max={3600}
            className="input"
            value={refresh}
            onChange={(e) => setRefresh(parseInt(e.target.value || '60', 10))}
          />
          <span className="field-help">
            0 turns auto-refresh off, so viewers&apos; filter selections stay put. Otherwise 5–3600.
          </span>
        </label>
      </div>
      {update.error && (
        <div className="alert alert--danger">
          <pre>
            {JSON.stringify(
              (update.error as Error & { body?: unknown }).body ?? String(update.error),
              null,
              2,
            )}
          </pre>
        </div>
      )}
      {internalAdmin && <StandardPanel dashboardId={dashboardId} definition={definition} />}
    </div>
  );
}
